import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CreditCard, Ban } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

const parsePrice = (p) => parseFloat(String(p || "").replace(/[^0-9.]/g, "")) || 0;
const ITEM_STATUS = { new: "New", cooking: "Cooking", ready: "Ready", served: "Served" };
const ITEM_COLORS = {
  new: "bg-amber-400/10 text-amber-500 border-amber-400/30",
  cooking: "bg-blue-400/10 text-blue-500 border-blue-400/30",
  ready: "bg-emerald-400/10 text-emerald-500 border-emerald-400/30",
  served: "bg-zinc-400/10 text-zinc-400 border-zinc-400/30",
};

export default function OrdersTab({ restaurant }) {
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tables, setTables] = useState([]);
  const [menus, setMenus] = useState([]);
  const [newTable, setNewTable] = useState("");
  const [seat, setSeat] = useState("1");
  const [note, setNote] = useState("");

  const load = () => api.get(`/restaurants/${restaurant.id}/orders`, { params: { status: "open" } })
    .then((r) => {
      setOrders(r.data);
      if (selectedId) {
        const cur = r.data.find((o) => o.id === selectedId);
        if (!cur) setSelectedId(null);
      }
    }).catch(() => {});

  useEffect(() => {
    load();
    api.get(`/restaurants/${restaurant.id}/tables`).then((r) => setTables(r.data.filter((t) => t.active))).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/menus`).then((r) => setMenus(r.data)).catch(() => {});
  }, [restaurant.id]);

  const selected = orders.find((o) => o.id === selectedId);

  const refreshSelected = (order) => {
    setOrders((prev) => prev.map((o) => (o.id === order.id ? order : o)));
  };

  const createOrder = async () => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/orders`, { table_id: newTable });
      toast.success(`Order opened${data.table_label ? ` for ${data.table_label}` : ""}`);
      setNewTable("");
      setSelectedId(data.id);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addItem = async (menuItem) => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/orders/${selectedId}/items`, {
        name: menuItem.name, price: parsePrice(menuItem.price), qty: 1,
        seat: Number(seat), station: menuItem.station || "kitchen", notes: note,
      });
      setNote("");
      refreshSelected(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const removeItem = async (iid) => {
    try {
      const { data } = await api.delete(`/restaurants/${restaurant.id}/orders/${selectedId}/items/${iid}`);
      refreshSelected(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const pay = async (seatNum) => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/orders/${selectedId}/pay`,
        seatNum ? { seat: seatNum } : {});
      toast.success(seatNum ? `Seat ${seatNum} paid` : "Order paid in full");
      refreshSelected(data);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const voidOrder = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/orders/${selectedId}/void`);
      toast.success("Order voided");
      setSelectedId(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const seatTotals = {};
  if (selected) {
    for (const i of selected.items) {
      const s = i.seat || 1;
      seatTotals[s] = seatTotals[s] || { total: 0, paid: true };
      seatTotals[s].total += i.price * i.qty;
      if (!i.paid) seatTotals[s].paid = false;
    }
  }

  return (
    <div data-testid="orders-tab" className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-4 space-y-4">
        <div className="rounded-2xl border border-border/60 bg-card p-5">
          <h3 className="font-outfit font-medium">New order</h3>
          <div className="flex gap-2 mt-3">
            <Select value={newTable} onValueChange={setNewTable}>
              <SelectTrigger data-testid="new-order-table" className="bg-background border-border/60">
                <SelectValue placeholder="Select table" />
              </SelectTrigger>
              <SelectContent>
                {tables.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label} ({t.capacity})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="open-order-btn" onClick={createOrder} disabled={!newTable} className="rounded-full shrink-0">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="space-y-2.5">
          {orders.map((o) => (
            <button key={o.id} data-testid={`order-card-${o.id}`} onClick={() => setSelectedId(o.id)}
              className={`w-full text-left rounded-xl border p-4 transition-colors ${
                selectedId === o.id ? "border-primary/60 bg-primary/5" : "border-border/60 bg-card hover:border-border"
              }`}>
              <div className="flex items-center justify-between">
                <p className="font-medium text-sm">{o.table_label || o.label || "Order"} · {o.items.length} items</p>
                <span className="font-outfit font-semibold">£{o.open_total.toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(o.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                {o.paid_total > 0 && ` · £${o.paid_total.toFixed(2)} paid`}
              </p>
            </button>
          ))}
          {orders.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-orders-msg">No open orders.</p>}
        </div>
      </div>

      <div className="lg:col-span-8">
        {!selected ? (
          <div className="rounded-2xl border border-border/60 bg-card p-12 text-center text-muted-foreground text-sm" data-testid="select-order-hint">
            Open an order from the left to start taking items.
          </div>
        ) : (
          <div className="rounded-2xl border border-border/60 bg-card p-5" data-testid="order-detail">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-outfit font-medium text-lg">{selected.table_label || "Order"}</h3>
              <div className="flex gap-2">
                <Button data-testid="void-order-btn" size="sm" variant="ghost" className="text-destructive rounded-full" onClick={voidOrder}>
                  <Ban className="w-4 h-4 mr-1" /> Void
                </Button>
                <Button data-testid="pay-all-btn" size="sm" className="rounded-full" onClick={() => pay(null)}
                  disabled={selected.open_total === 0}>
                  <CreditCard className="w-4 h-4 mr-1" /> Pay all · £{selected.open_total.toFixed(2)}
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3 mt-5 pb-4 border-b border-border/40">
              <div>
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Seat</Label>
                <Select value={seat} onValueChange={setSeat}>
                  <SelectTrigger data-testid="item-seat-select" className="mt-1 bg-background border-border/60 w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>Seat {n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Modifiers / notes (allergies, temps)</Label>
                <Input data-testid="item-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. no nuts, medium-rare" className="mt-1 bg-background border-border/60" />
              </div>
            </div>

            <div className="mt-4 max-h-48 overflow-y-auto space-y-1.5">
              {menus.map((m) => (
                <div key={m.id}>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-primary mt-2 mb-1.5">{m.name}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {m.sections.flatMap((s) => s.items).map((it, ii) => (
                      <button key={ii} data-testid={`menu-item-${it.name.replace(/\s+/g, "-")}`} onClick={() => addItem(it)}
                        className="rounded-lg border border-border/60 px-3 py-1.5 text-xs hover:border-primary/60 hover:bg-primary/5 transition-colors">
                        {it.name} {it.price && <span className="text-muted-foreground">· {it.price}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {menus.length === 0 && <p className="text-sm text-muted-foreground">No menus yet — create one in the Menus tab to speed up order entry.</p>}
            </div>

            <div className="mt-5 space-y-2">
              {selected.items.map((i) => (
                <div key={i.id} data-testid={`order-item-${i.id}`}
                  className="flex items-center justify-between rounded-xl border border-border/40 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{i.qty}× {i.name}
                      {i.notes && <span className="text-amber-500 text-xs ml-2">({i.notes})</span>}
                    </p>
                    <p className="text-xs text-muted-foreground">Seat {i.seat || 1} · {i.station}</p>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <Badge variant="outline" className={ITEM_COLORS[i.status]}>{ITEM_STATUS[i.status]}</Badge>
                    <span className="text-sm font-outfit w-16 text-right">£{(i.price * i.qty).toFixed(2)}</span>
                    {i.paid ? (
                      <Badge variant="outline" className="text-emerald-500 border-emerald-500/30">paid</Badge>
                    ) : (
                      <button data-testid={`remove-item-${i.id}`} onClick={() => removeItem(i.id)}
                        className="text-muted-foreground hover:text-destructive transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {selected.items.length === 0 && <p className="text-sm text-muted-foreground py-3">No items yet — tap menu items above to add.</p>}
            </div>

            {Object.keys(seatTotals).length > 1 && (
              <div className="mt-5 pt-4 border-t border-border/40 flex flex-wrap gap-3" data-testid="split-bill-row">
                {Object.entries(seatTotals).sort().map(([s, t]) => (
                  <div key={s} className="rounded-xl border border-border/40 px-4 py-2.5 flex items-center gap-3">
                    <span className="text-sm">Seat {s}: <span className="font-outfit font-medium">£{t.total.toFixed(2)}</span></span>
                    {!t.paid && (
                      <Button data-testid={`pay-seat-${s}`} size="sm" variant="outline" className="rounded-full h-7 text-xs"
                        onClick={() => pay(Number(s))}>Pay seat</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
